import { HttpClient } from '@angular/common/http';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { APP_CONFIG } from '../config/app-config.value';
import { type NavEntry, navFor, navForPermissions } from '../navigation';
import { ROLES, type RoleId, canWrite } from '../models/role.model';

const ROLE_KEY = 'uniit.demo.role';

/** État du handoff d'authentification en mode branché (cf. `SessionStore.authStatus`). */
export type AuthStatus = 'loading' | 'ready' | 'redirecting' | 'error';

/** Identité renvoyée par `GET /api/me` (droits lus dans le JWT UniPortail validé). */
interface MeResponse {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly permissions: readonly string[];
  readonly scopedCollaboratorIds: readonly string[];
}

/**
 * Session courante : rôle et — en mode branché — identité réelle.
 *
 * - **Mode `demo`** (`npm start`) : le rôle vient du sélecteur de la maquette, qui reste fonctionnel
 *   pour développer les écrans. C'est le seul mode où l'on « voit en tant que ».
 * - **Mode `api`** : le sélecteur disparaît ; l'identité vient de `GET /api/me` (claims du JWT). Un
 *   401 renvoie au portail. Le rôle est **dérivé des permissions** à titre transitoire — la
 *   navigation réellement pilotée par les permissions arrive au Lot 3 (L3-04) ; ici on retire le
 *   rôle simulé et on branche l'identité réelle.
 */
@Injectable({ providedIn: 'root' })
export class SessionStore {
  private readonly http = inject(HttpClient);
  private readonly live = APP_CONFIG.dataSource === 'api';

  private readonly _role = signal<RoleId>(readRole());
  private readonly _identity = signal<MeResponse | null>(null);

  /**
   * État du handoff d'authentification, en mode branché uniquement :
   * - `loading`     : `GET /api/me` en cours — l'app affiche un écran d'attente plutôt qu'un shell
   *                   qui clignoterait le temps que les droits arrivent ;
   * - `ready`       : identité chargée (ou mode démo) — le shell s'affiche ;
   * - `redirecting` : 401 reçu, on part vers le portail — écran « Redirection vers UniPortail… » ;
   * - `error`       : `/api/me` injoignable (hors 401) — écran d'erreur avec « Réessayer ».
   * En mode démo, l'état vaut `ready` d'emblée : il n'y a aucun réseau.
   */
  private readonly _authStatus = signal<AuthStatus>(this.live ? 'loading' : 'ready');
  readonly authStatus = this._authStatus.asReadonly();

  readonly role = this._role.asReadonly();

  readonly currentRole = computed(() => ROLES.find((r) => r.id === this._role())!);
  readonly canWrite = computed(() => canWrite(this._role()));

  /**
   * Nom affiché : en mode branché, le nom réel du token (claim `name`), à défaut l'email ; en démo,
   * l'utilisateur de la maquette. C'est ce nom qui alimente l'avatar (initiales) et l'en-tête.
   */
  readonly displayName = computed(
    () => this._identity()?.name ?? this._identity()?.email ?? this.currentRole().user,
  );

  /** Permissions réelles (mode branché) — pilotent navigation et actions (L3-04). */
  readonly permissions = computed<ReadonlySet<string>>(
    () => new Set(this._identity()?.permissions ?? []),
  );

  /**
   * Entrées de navigation visibles : en mode branché, celles dont la permission figure dans le token
   * (L3-04) ; en démo, celles du rôle simulé. Le contrôle réel reste côté serveur (L3-02).
   */
  readonly navEntries = computed<readonly NavEntry[]>(() =>
    this.live ? navForPermissions(this.permissions()) : navFor(this._role()),
  );

  /**
   * Vrai si l'utilisateur peut exercer l'action. Mode branché : permission réelle du token. Démo :
   * dérivé du rôle (aucune permission simulée) — l'écriture est réservée à l'admin et au gestionnaire.
   */
  can(permission: string): boolean {
    return this.live ? this.permissions().has(permission) : this.canWrite();
  }

  /** Initiales de l'utilisateur courant, pour l'avatar du header. */
  readonly initials = computed(() => initialsOf(this.displayName()));

  constructor() {
    effect(() => persist(ROLE_KEY, this._role()));

    if (this.live) {
      // Handoff : le portail nous renvoie sur l'URL de l'app avec le JWT en query (`?token=…`) — il
      // ne connaît que l'URL du module, pas le chemin du callback. C'est donc à l'app de relayer ce
      // token au callback backend (`/api/auth/portal-callback`), qui valide le JWT, pose le cookie de
      // session `uniit_jwt` et redirige vers `/`. Sans ce relais, aucun cookie n'est posé, `/api/me`
      // reste 401 et l'app reboucle indéfiniment sur la redirection portail.
      const token = new URLSearchParams(window.location.search).get('token');
      if (token) {
        this._authStatus.set('redirecting');
        // `replace` (et non `assign`) : le JWT ne doit pas rester dans l'historique du navigateur.
        window.location.replace(
          `${APP_CONFIG.apiBaseUrl}/auth/portal-callback?token=${encodeURIComponent(token)}`,
        );
        return;
      }

      void this.loadRealIdentity();
    }
  }

  setRole(role: RoleId): void {
    this._role.set(role);
  }

  /** Relance le handoff après une erreur (bouton « Réessayer » de l'écran d'attente). */
  retry(): void {
    if (this.live) {
      this._authStatus.set('loading');
      void this.loadRealIdentity();
    }
  }

  /**
   * Charge l'identité réelle en mode branché. Un 401 signifie « non authentifié » : on renvoie au
   * callback de handoff, que le serveur redirige vers la page de connexion du portail.
   */
  private async loadRealIdentity(): Promise<void> {
    try {
      const me = await firstValueFrom(
        this.http.get<MeResponse>(`${APP_CONFIG.apiBaseUrl}/me`),
      );
      this._identity.set(me);
      this._role.set(roleFromPermissions(new Set(me.permissions)));
      this._authStatus.set('ready');
    } catch (error) {
      if (isUnauthorized(error)) {
        // On montre brièvement l'écran de redirection avant de quitter l'app : un
        // `window.location.assign` sec laisserait un écran figé sans explication.
        this._authStatus.set('redirecting');
        setTimeout(() => void this.redirectToPortal(), 600);
      } else {
        // Autre erreur (API injoignable) : écran d'erreur avec « Réessayer », sans casser l'app.
        this._authStatus.set('error');
      }
    }
  }

  /**
   * Renvoie l'utilisateur vers le handoff SSO d'UniPortail avec le slug du module. On lit la racine
   * du portail auprès de l'API (`GET /api/auth/portal-url`, anonyme), puis on part vers
   * `{portail}/auth/handoff?to=uniit` — le portail vérifie sa session et 302 vers
   * `/api/auth/portal-callback?token=…`, qui pose le cookie de session et ramène à l'app
   * (SPEC-ACC-002, package Unipros.Portail.Auth).
   */
  private async redirectToPortal(): Promise<void> {
    try {
      const { url } = await firstValueFrom(
        this.http.get<{ url: string }>(`${APP_CONFIG.apiBaseUrl}/auth/portal-url`),
      );
      window.location.assign(`${url}/auth/handoff?to=uniit`);
    } catch {
      // Portail introuvable : dernier recours, le callback local que le serveur redirige lui-même.
      window.location.assign(`${APP_CONFIG.apiBaseUrl}/auth/portal-callback`);
    }
  }
}

/**
 * Correspondance transitoire permissions → rôle de navigation, le temps que L3-04 pilote la
 * navigation directement par les permissions. Volontairement grossière : elle ne sert qu'à ce que
 * les écrans restent cohérents entre L2 et L3.
 */
function roleFromPermissions(permissions: ReadonlySet<string>): RoleId {
  if (permissions.has('*') || permissions.has('uniit:asset:read-all')) return 'admin';
  if (permissions.has('uniit:asset:write') || permissions.has('uniit:collaborator:write')) return 'gestion';
  if (permissions.has('uniit:collaborator:read')) return 'manager';
  return 'collab';
}

function initialsOf(name: string): string {
  return name
    .split(/[ .@]/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function isUnauthorized(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'status' in error && error.status === 401;
}

function readRole(): RoleId {
  const stored = read(ROLE_KEY);
  return ROLES.some((role) => role.id === stored) ? (stored as RoleId) : 'admin';
}

/** sessionStorage peut être indisponible (navigation privée stricte) : on dégrade sans casser. */
function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function persist(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Pas de persistance disponible : la session reste valable, elle ne survivra pas au rechargement.
  }
}
